import { useState, useEffect } from 'react';
import { Calculator, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { settingsAPI } from '../lib/settings-api';

interface AffordabilityCalculatorProps {
  propertyPrice?: number;
}

export function AffordabilityCalculator({ propertyPrice }: AffordabilityCalculatorProps) {
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [inputs, setInputs] = useState({
    price: propertyPrice || 0,
    deposit: 0,
    interestRate: 11.75, // Will be loaded from settings
    term: 20, // Will be loaded from settings
    monthlyIncome: 0,
  });

  const [results, setResults] = useState({
    monthlyRepayment: 0,
    totalInterest: 0,
    affordabilityRatio: 0,
    canAfford: false,
  });

  // Load settings from backend
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const { settings } = await settingsAPI.getSettings();
        setInputs(prev => ({
          ...prev,
          interestRate: settings.economicIndicators.interestRate,
          term: settings.calculatorDefaults.loanTerm,
        }));
        console.log('✅ Calculator settings loaded:', {
          interestRate: settings.economicIndicators.interestRate,
          loanTerm: settings.calculatorDefaults.loanTerm
        });
      } catch (error) {
        logger.error('Failed to load calculator settings', error as Error, undefined, 'AffordabilityCalculator');
      } finally {
        setIsLoadingSettings(false);
      }
    };
    
    loadSettings();
  }, []);

  useEffect(() => {
    if (propertyPrice) {
      setInputs(prev => ({ ...prev, price: propertyPrice }));
    }
  }, [propertyPrice]);

  useEffect(() => {
    if (!isLoadingSettings) {
      calculateAffordability();
    }
  }, [inputs, isLoadingSettings]);

  const calculateAffordability = () => {
    const { price, deposit, interestRate, term, monthlyIncome } = inputs;
    
    if (price <= 0) return;

    const loanAmount = price - deposit;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = term * 12;

    // Monthly payment calculation (PMT formula)
    let monthlyRepayment = 0;
    if (monthlyRate > 0) {
      monthlyRepayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    } else {
      monthlyRepayment = loanAmount / numberOfPayments;
    }

    const totalPaid = monthlyRepayment * numberOfPayments;
    const totalInterest = totalPaid - loanAmount;
    
    // Affordability check (typically 30% of gross income)
    const affordabilityRatio = monthlyIncome > 0 ? (monthlyRepayment / monthlyIncome) * 100 : 0;
    const canAfford = affordabilityRatio <= 30 && monthlyIncome > 0;

    setResults({
      monthlyRepayment,
      totalInterest,
      affordabilityRatio,
      canAfford,
    });
  };

  const updateInput = (field: keyof typeof inputs, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getAffordabilityStatus = () => {
    if (inputs.monthlyIncome === 0) return null;
    
    if (results.canAfford) {
      return <Badge className="bg-green-100 text-green-800">Affordable</Badge>;
    } else if (results.affordabilityRatio <= 35) {
      return <Badge variant="secondary">Marginal</Badge>;
    } else {
      return <Badge variant="destructive">Not Affordable</Badge>;
    }
  };

  const getAffordabilityHint = () => {
    if (inputs.monthlyIncome === 0) {
      return "Enter your monthly income to see affordability assessment.";
    }
    
    if (results.canAfford) {
      return "This property appears to be within your budget based on the 30% rule.";
    } else if (results.affordabilityRatio <= 35) {
      return "This property is on the edge of affordability. Consider a larger deposit.";
    } else {
      const maxAffordable = inputs.monthlyIncome * 0.3;
      return `Consider properties with monthly payments below ${formatCurrency(maxAffordable)}.`;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calculator className="w-5 h-5" />
          Affordability Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="price">Property Price</Label>
            <Input
              id="price"
              type="number"
              value={inputs.price || ''}
              onChange={(e) => updateInput('price', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="deposit">Deposit</Label>
            <Input
              id="deposit"
              type="number"
              value={inputs.deposit || ''}
              onChange={(e) => updateInput('deposit', parseInt(e.target.value) || 0)}
              placeholder="0"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="interestRate">Interest Rate (%)</Label>
            <Input
              id="interestRate"
              type="number"
              step="0.25"
              value={inputs.interestRate}
              onChange={(e) => updateInput('interestRate', parseFloat(e.target.value) || 0)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="term">Loan Term (years)</Label>
            <Input
              id="term"
              type="number"
              value={inputs.term}
              onChange={(e) => updateInput('term', parseInt(e.target.value) || 0)}
            />
          </div>

          <div className="space-y-2 sm:col-span-2">
            <Label htmlFor="monthlyIncome">Monthly Income (optional)</Label>
            <Input
              id="monthlyIncome"
              type="number"
              value={inputs.monthlyIncome || ''}
              onChange={(e) => updateInput('monthlyIncome', parseInt(e.target.value) || 0)}
              placeholder="Enter for affordability check"
            />
          </div>
        </div>

        {/* Results */}
        {inputs.price > 0 && (
          <div className="mt-6 p-4 border rounded-lg bg-muted/50">
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium">Monthly Repayment:</span>
                <span className="text-lg font-bold">{formatCurrency(results.monthlyRepayment)}</span>
              </div>

              <div className="flex justify-between items-center text-sm">
                <span>Total Interest:</span>
                <span>{formatCurrency(results.totalInterest)}</span>
              </div>

              {inputs.monthlyIncome > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span>Debt-to-Income Ratio:</span>
                  <div className="flex items-center gap-2">
                    <span>{results.affordabilityRatio.toFixed(1)}%</span>
                    {getAffordabilityStatus()}
                  </div>
                </div>
              )}

              <div className="pt-2 border-t text-sm text-muted-foreground">
                {getAffordabilityHint()}
              </div>
            </div>
          </div>
        )}

        <div className="text-xs text-muted-foreground">
          <strong>Disclaimer:</strong> This is an estimate only. Actual loan terms, rates, and approval depend on various factors including credit score, income verification, and bank policies.
        </div>
      </CardContent>
    </Card>
  );
}